import { PufferWithdrawalManagerAbi } from '../abis/puffer'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, type LstWithdrawalReader } from './shared'

/**
 * Puffer pufETH — log-free via caller-supplied IDs.
 *
 * The reader expects `knownIds` to be the list of `withdrawalIdx`
 * values captured from the `WithdrawalRequested` event in the
 * withdraw tx receipt at request time.
 *
 * Per request: multicall `getWithdrawal(idx)` per id plus a single
 * `getFinalizedWithdrawalBatch()` + `BATCH_SIZE()` for the
 * finalization frontier. A request is claimable iff
 * `floor(idx / BATCH_SIZE) <= finalizedBatch`.
 *
 * Returns `[]` when no IDs are supplied.
 */
export const readerPuffer: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry, knownIds) => {
    if (!entry.withdrawalContract) return []
    if (!knownIds || knownIds.length === 0) return []

    const ids = knownIds.filter((v): v is bigint => typeof v === 'bigint')
    if (ids.length === 0) return []

    const detailCalls = ids.map((id) => ({
      address: entry.withdrawalContract!,
      name: 'getWithdrawal',
      params: [id],
    }))
    const calls = [
      ...detailCalls,
      {
        address: entry.withdrawalContract,
        name: 'getFinalizedWithdrawalBatch',
        params: [],
      },
      {
        address: entry.withdrawalContract,
        name: 'BATCH_SIZE',
        params: [],
      },
    ]
    const results = (await multicallRetry({
      chain: chainId,
      calls: calls as unknown as never[],
      abi: calls.map(() => PufferWithdrawalManagerAbi) as unknown as never,
    })) as unknown[]

    const finalizedBatch = toBigInt(results[detailCalls.length]) ?? 0n
    const batchSize = toBigInt(results[detailCalls.length + 1]) ?? 0n

    const out: LstWithdrawalRequest[] = []
    const lcUser = user.toLowerCase()
    for (let i = 0; i < ids.length; i++) {
      const d = results[i] as
        | { pufETHAmount?: unknown; recipient?: unknown }
        | unknown[]
        | undefined
      if (!d) continue
      const recipient = Array.isArray(d)
        ? (d[2] as string | undefined)
        : (d.recipient as string | undefined)
      if (typeof recipient === 'string' && recipient.toLowerCase() !== lcUser) {
        continue
      }
      const pufETHAmount = Array.isArray(d)
        ? toBigInt(d[0])
        : toBigInt(d.pufETHAmount)
      if (pufETHAmount === undefined) continue
      const batchIdx = batchSize > 0n ? ids[i] / batchSize : 0n
      const claimable = batchIdx <= finalizedBatch
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: ids[i].toString(),
        amountUnderlying: pufETHAmount.toString(),
        status: claimable ? 'claimable' : 'pending',
      })
    }
    return out
  },
}
